import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dates";
import {
  Lightbulb, Plus, ClipboardList, Sparkles, StickyNote, CheckCircle2, Clock, Circle,
  Pencil, Trash2, Lock, Search, MessageSquare, Send, X, Filter,
} from "lucide-react";

const TYPES = [
  { key: "note", label: "Note", icon: StickyNote, ring: "ring-slate-200", badge: "text-slate-700 bg-slate-100 border-slate-200", accent: "border-l-slate-400" },
  { key: "action", label: "Action", icon: ClipboardList, ring: "ring-amber-200", badge: "text-amber-800 bg-amber-100 border-amber-200", accent: "border-l-amber-500" },
  { key: "recommendation", label: "Recommendation", icon: Sparkles, ring: "ring-cyan-200", badge: "text-cyan-800 bg-cyan-100 border-cyan-200", accent: "border-l-cyan-500" },
];
const STATUSES = [
  { key: "open", label: "Open", icon: Circle, color: "text-slate-600" },
  { key: "in_progress", label: "In progress", icon: Clock, color: "text-amber-600" },
  { key: "done", label: "Done", icon: CheckCircle2, color: "text-emerald-600" },
];
const PRIORITIES = ["Low", "Medium", "High"];
const typeMeta = (t) => TYPES.find((x) => x.key === t) || TYPES[0];
const statusMeta = (s) => STATUSES.find((x) => x.key === s) || STATUSES[0];

export default function AdvisorNotes() {
  const { user, tenant } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/advisor/notes"); setNotes(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    let out = [...notes];
    if (filter !== "all") out = out.filter((n) => n.type === filter);
    if (statusFilter !== "all") out = out.filter((n) => n.status === statusFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((n) => (n.title || "").toLowerCase().includes(needle) || (n.body || "").toLowerCase().includes(needle));
    }
    if (sort === "newest") out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "priority") {
      const r = { High: 3, Medium: 2, Low: 1 };
      out.sort((a, b) => (r[b.priority] || 0) - (r[a.priority] || 0));
    } else if (sort === "due") {
      out.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    }
    return out;
  }, [notes, filter, statusFilter, q, sort]);

  const counts = useMemo(() => {
    const c = { all: notes.length };
    TYPES.forEach((t) => { c[t.key] = notes.filter((n) => n.type === t.key).length; });
    return c;
  }, [notes]);

  const remove = async (id) => {
    try { await api.delete(`/advisor/notes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cycleStatus = async (n) => {
    if (!isAdmin) return;
    const order = ["open", "in_progress", "done"];
    const next = order[(order.indexOf(n.status) + 1) % order.length];
    try {
      await api.patch(`/advisor/notes/${n.id}`, { status: next });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="advisor-page">
      <PageHeader
        kicker="Advisor notes"
        title="Advisor Notes"
        description={isAdmin
          ? `Notes, actions and recommendations for ${tenant?.name || "your tenant"} — everyone can read and comment; only admins edit.`
          : `From your assigned advisor. You can comment on any entry — your admin will action it.`}
        actions={isAdmin ? (
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setDialog({ open: true, editing: null })} data-testid="advisor-note-new">
            <Plus className="h-4 w-4 mr-1.5" /> New Entry
          </Button>
        ) : (
          <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-500 border border-slate-200 rounded-full px-3 py-1.5">
            <Lock className="h-3 w-3" /> Comment-only
          </div>
        )}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="advisor-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles + bodies…" className="pl-9 h-9" data-testid="advisor-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="advisor-status-filter"><Filter className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="advisor-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
            <SelectItem value="due">Due date</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="advisor-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`All`} count={counts.all} testid="advisor-filter-all" />
        {TYPES.map((t) => (
          <FilterChip key={t.key} active={filter === t.key} onClick={() => setFilter(t.key)} label={t.label} count={counts[t.key] || 0} icon={t.icon} testid={`advisor-filter-${t.key}`} />
        ))}
      </div>

      {loading ? <LoadingSkeleton /> : filtered.length === 0 ? (
        <EmptyState icon={Lightbulb}
          title={notes.length === 0 ? (isAdmin ? "No advisor entries yet" : "Nothing from your advisor yet") : "No entries match your filters"}
          description={notes.length === 0 ? (isAdmin ? "Capture notes, actions or recommendations from your last advisory session." : "Your advisor hasn't posted anything for your tenant yet.") : "Try clearing the search or switching filters."} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="advisor-note-list">
          {filtered.map((n) => {
            const tm = typeMeta(n.type); const sm = statusMeta(n.status); const Icon = tm.icon; const SIcon = sm.icon;
            return (
              <Card key={n.id} onClick={() => setDetail(n.id)}
                className={`p-5 bg-white border border-slate-200 border-l-4 ${tm.accent} hover:shadow-md hover:border-slate-300 cursor-pointer transition-all`}
                data-testid={`advisor-note-${n.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 border ${tm.badge}`}>
                    <Icon className="h-3 w-3" /> {tm.label}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); cycleStatus(n); }}
                    className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${sm.color} ${isAdmin ? "hover:underline" : "cursor-default"}`}
                    disabled={!isAdmin}
                    title={isAdmin ? "Click to advance status" : sm.label}
                  >
                    <SIcon className="h-3 w-3" /> {sm.label}
                  </button>
                </div>
                <h3 className="font-display font-semibold text-slate-900 text-[15px] leading-snug">{n.title}</h3>
                {n.body && <p className="text-[13px] text-slate-600 mt-2 leading-relaxed line-clamp-3">{n.body}</p>}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                  <div className="font-mono">
                    <div>{n.created_by_name || "Advisor"}</div>
                    <div className="text-[10px] opacity-70">{formatDateTime(n.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {n.priority && <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${n.priority === "High" ? "bg-rose-100 text-rose-700" : n.priority === "Medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{n.priority}</span>}
                    {n.due_date && <span className="text-[10px] font-mono">Due {formatDate(n.due_date)}</span>}
                    <span className="inline-flex items-center gap-0.5 text-[10.5px] font-mono text-slate-600" title="Comments">
                      <MessageSquare className="h-3 w-3" /> {n.comment_count || 0}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {dialog.open && isAdmin && (
        <NoteDialog
          existing={dialog.editing}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
        />
      )}

      {detail && (
        <NoteDetailDrawer
          noteId={detail}
          isAdmin={isAdmin}
          currentUser={user}
          onClose={() => setDetail(null)}
          onEdit={(n) => { setDetail(null); setDialog({ open: true, editing: n }); }}
          onDelete={async (id) => { await remove(id); setDetail(null); }}
          onChanged={load}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count, icon: Icon, testid }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium border inline-flex items-center gap-1.5 transition-all ${active ? "bg-[#0A1329] text-white border-[#0A1329]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
      data-testid={testid}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label} <span className="text-[11px] font-mono opacity-70">{count}</span>
    </button>
  );
}

function NoteDialog({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(existing ? { ...existing, due_date: existing.due_date ? existing.due_date.slice(0, 10) : "" } : { title: "", body: "", type: "note", status: "open", priority: "Medium", due_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { title: form.title.trim(), body: form.body?.trim() || null, type: form.type, status: form.status, priority: form.priority, due_date: form.due_date ? new Date(form.due_date).toISOString() : null };
      if (isEdit) await api.patch(`/advisor/notes/${existing.id}`, payload);
      else await api.post("/advisor/notes", payload);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="advisor-note-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit advisor entry" : "New advisor entry"}</DialogTitle>
          <DialogDescription>Notes, actions and recommendations are visible to every tenant user — only admins can edit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Board wants ISO 27001 Stage 1 by June" data-testid="advisor-note-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="advisor-note-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="advisor-note-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="advisor-note-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Due date</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="advisor-note-due" /></div>
          </div>
          <div><Label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Details</Label>
            <Textarea rows={5} value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Context, next steps, links…" data-testid="advisor-note-body" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="advisor-note-cancel">Cancel</Button>
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" disabled={busy} onClick={submit} data-testid="advisor-note-save">{busy ? "Saving…" : isEdit ? "Save changes" : "Create entry"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoteDetailDrawer({ noteId, isAdmin, currentUser, onClose, onEdit, onDelete, onChanged }) {
  const [note, setNote] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: notes }, { data: cs }] = await Promise.all([
        api.get(`/advisor/notes`), api.get(`/advisor/notes/${noteId}/comments`),
      ]);
      setNote(notes.find((n) => n.id === noteId));
      setComments(cs || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [noteId]); // eslint-disable-line

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/advisor/notes/${noteId}/comments`, { body });
      setDraft("");
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const saveEdit = async (cid) => {
    if (!editingBody.trim()) return;
    try {
      await api.patch(`/advisor/notes/${noteId}/comments/${cid}`, { body: editingBody.trim() });
      setEditingId(null); setEditingBody("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeComment = async (cid) => {
    try { await api.delete(`/advisor/notes/${noteId}/comments/${cid}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const tm = note ? typeMeta(note.type) : TYPES[0];
  const sm = note ? statusMeta(note.status) : STATUSES[0];
  const Icon = tm.icon;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col bg-white" data-testid="advisor-note-detail">
        {loading || !note ? (
          <div className="p-6"><LoadingSkeleton /></div>
        ) : (
          <>
            <div className={`p-6 border-b border-slate-100 bg-slate-50/40 border-l-4 ${tm.accent}`}>
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 border ${tm.badge}`}>
                  <Icon className="h-3 w-3" /> {tm.label}
                </span>
                <div className="flex items-center gap-2">
                  {isAdmin && <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onEdit(note)} data-testid="advisor-note-detail-edit"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid="advisor-note-detail-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                          <AlertDialogDescription>“{note.title}” and every comment on it will be removed.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => onDelete(note.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <button onClick={onClose} className="p-1 rounded hover:bg-slate-200"><X className="h-4 w-4 text-slate-600" /></button>
                </div>
              </div>
              <h2 className="mt-3 font-display font-bold text-[20px] text-slate-900 leading-snug">{note.title}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500 font-mono">
                <span className={sm.color}><sm.icon className="inline h-3 w-3 mr-1" />{sm.label}</span>
                {note.priority && <span>Priority · {note.priority}</span>}
                {note.due_date && <span>Due · {formatDate(note.due_date)}</span>}
                <span>{note.created_by_name || "Advisor"} · {formatDateTime(note.created_at)}</span>
              </div>
              {note.body && <p className="mt-4 text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap">{note.body}</p>}
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="advisor-note-comments">
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Discussion · {comments.length}</div>
              {comments.length === 0 && <div className="text-[13px] text-slate-500 italic">No comments yet. Start the discussion below.</div>}
              {comments.map((c) => {
                const canEdit = c.author_id === currentUser?.id;
                const canDelete = canEdit || isAdmin;
                const initials = (c.author_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <div key={c.id} className="flex gap-3" data-testid={`advisor-comment-${c.id.slice(0, 8)}`}>
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-[10.5px] font-semibold shrink-0">{initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11.5px] text-slate-600">
                        <span className="font-semibold text-slate-900">{c.author_name}</span>
                        {c.author_role && <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{c.author_role}</span>}
                        <span className="text-[10px] text-slate-400 font-mono">{formatDateTime(c.created_at)}{c.edited_at ? " · edited" : ""}</span>
                      </div>
                      {editingId === c.id ? (
                        <div className="mt-1.5">
                          <Textarea rows={3} value={editingBody} onChange={(e) => setEditingBody(e.target.value)} className="text-[13px]" />
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" className="h-7 bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => saveEdit(c.id)}>Save</Button>
                            <Button size="sm" variant="ghost" className="h-7" onClick={() => { setEditingId(null); setEditingBody(""); }}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mt-1 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">{c.body}</div>
                          {(canEdit || canDelete) && (
                            <div className="mt-1 flex gap-3 text-[11px]">
                              {canEdit && <button onClick={() => { setEditingId(c.id); setEditingBody(c.body); }} className="text-slate-500 hover:text-slate-900">Edit</button>}
                              {canDelete && <button onClick={() => removeComment(c.id)} className="text-rose-500 hover:text-rose-700">Delete</button>}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-slate-100 bg-white">
              <div className="flex gap-2">
                <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Comment as your team…" className="text-[13px] resize-none" data-testid="advisor-comment-input" />
                <Button className="self-end bg-[#0A1329] hover:bg-[#162347] text-white" disabled={busy || !draft.trim()} onClick={send} data-testid="advisor-comment-send"><Send className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="mt-1 text-[10.5px] text-slate-400 font-mono uppercase tracking-widest">Comments are visible to all tenant users</div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
